// @ts-ignore
import { GoogleGenerativeAI, Part } from "@google/generative-ai";
import { UserProfile, GenerateContentResponse } from "../types";
import { API_CONFIG, GEMINI_CONFIG, API_KEYS } from '../config';
import { Logger } from "./logger";

// Initialize the GoogleGenerativeAI client lazily to allow for runtime overrides of API_KEYS
let _aiClient: GoogleGenerativeAI | null = null;

export const getAIClient = (): GoogleGenerativeAI => {
    if (!_aiClient) {
        if (!API_KEYS.gemini || API_KEYS.gemini === 'PLACEHOLDER' || API_KEYS.gemini.length < 10) {
            Logger.warn('Gemini API key is missing or invalid in Shared/config. Using fallback initialization. Please check your environment variables (GEMINI_API_KEY/VITE_GEMINI_API_KEY).');
        }
        _aiClient = new GoogleGenerativeAI(API_KEYS.gemini || 'fallback_key_to_prevent_crash');
    }
    return _aiClient;
};

/**
 * Exported AI client instance.
 * Uses a Proxy to ensure that any calls to the client are directed to the lazily-initialized instance,
 * picking up the latest API_KEYS state.
 */
export const ai: GoogleGenerativeAI = new Proxy({} as GoogleGenerativeAI, {
    get: (_target, prop) => {
        const client = getAIClient();
        const value = (client as any)[prop];
        if (typeof value === 'function') {
            return value.bind(client);
        }
        return value;
    }
});

/**
 * Helper to construct a personalization context string from the user profile.
 * Unifies both Mobile and UIX personalization logic.
 */
export function buildPersonalizationContext(profile?: UserProfile): string {
    if (!profile) return "";

    const parts = [];

    // Physical Attributes
    if (profile.bodyType) parts.push(`Body Type: ${profile.bodyType}`);
    if (profile.ageRange) parts.push(`Age Range: ${profile.ageRange}`);
    if (profile.skinTone) parts.push(`Skin Tone: ${profile.skinTone}`);
    if (profile.hairColor) parts.push(`Hair Color: ${profile.hairColor}`);
    if (profile.eyeColor) parts.push(`Eye Color: ${profile.eyeColor}`);
    if (profile.faceShape) parts.push(`Face Shape: ${profile.faceShape}`);

    // Measurements
    if (profile.measurements) {
        const m = profile.measurements;
        const mParts = [];
        if (m.height) mParts.push(`Height: ${m.height}`);
        if (m.weight) mParts.push(`Weight: ${m.weight}`);
        if (m.chest) mParts.push(`Chest: ${m.chest}`);
        if (m.waist) mParts.push(`Waist: ${m.waist}`);
        if (m.hips) mParts.push(`Hips: ${m.hips}`);
        if (mParts.length > 0) parts.push(`Measurements: ${mParts.join(', ')}`);
    }

    // Preferences
    if (profile.fashionGoals) parts.push(`Fashion Goals: ${profile.fashionGoals}`);
    if (profile.styleIcons) parts.push(`Style Inspiration: ${profile.styleIcons}`);
    if (profile.stylePreferences) {
        if (profile.stylePreferences.likedTags.length > 0) {
            parts.push(`Liked Styles: ${profile.stylePreferences.likedTags.join(', ')}`);
        }
        if (profile.stylePreferences.dislikedTags.length > 0) {
            parts.push(`Disliked/Avoid Styles: ${profile.stylePreferences.dislikedTags.join(', ')}`);
        }
    }
    if (profile.preferredColors && profile.preferredColors.length > 0) {
        parts.push(`Preferred Colors: ${profile.preferredColors.join(', ')}`);
    }

    if (parts.length === 0) return "";

    return `\n\n[PERSONALIZATION CONTEXT]\nIMPORTANT: Tailor the result to the following user profile:\n${parts.join('\n')}\nEnsure recommendations flatter their specific attributes and align with their goals.`;
}

/**
 * Standard system instruction for the fashion stylist persona.
 */
export const STYLIST_SYSTEM_INSTRUCTION = `
You are Constantina (also known as OnIt Stylist), a high-end AI fashion stylist and advisor.
Your tone is professional, inspiring, sophisticated, and encouraging.
You have a deep understanding of color theory, fabric quality, and timeless styling.
Always provide advice that is actionable and tailored to the user's specific context.
Avoid generic fashion clichés and focus on high-impact, sustainable wardrobe building.
`;

export const withStylistPersona = (prompt: string): string => {
    return `${STYLIST_SYSTEM_INSTRUCTION}\n\nTask: ${prompt}`;
};

/**
 * Unified image-to-base64 helper (Server-side/Node compatible if needed, but primarily for Browser)
 */
export async function urlToBase64(url: string): Promise<string> {
    if (url.startsWith('data:')) {
        return url.split(',')[1];
    }
    try {
        const response = await fetch(url);
        const blob = await response.blob();
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => {
                const result = reader.result as string;
                resolve(result.split(',')[1]);
            }
            reader.onerror = reject;
            reader.readAsDataURL(blob);
        });
    } catch (error) {
        Logger.error(`Failed to fetch image for base64 conversion: ${url}`, error);
        throw error;
    }
}

/**
 * Unified styling advice call using the Thinking model for deeper analysis.
 */
export async function getUnifiedStylingAdvice(
    prompt: string,
    image?: string,
    profile?: UserProfile,
    persona?: string,
    history: any[] = []
): Promise<{ text: string; history: any[] }> {
    const model = GEMINI_CONFIG.MULTIMODAL_MODEL || 'gemini-2.0-flash-thinking-exp';
    const messageParts: Part[] = [];

    if (image) {
        const base64 = image.includes(',') ? image.split(',')[1] : image;
        const mimeType = image.includes('data:') ? image.split(';')[0].split(':')[1] : 'image/jpeg';
        messageParts.push({
            inlineData: { data: base64, mimeType }
        });
    }

    messageParts.push({ text: prompt });

    const personalization = buildPersonalizationContext(profile);
    const personaInstruction = persona ? `\n\n[STYLIST PERSONA]\nYou are currently adopting the persona: ${persona}.` : "";

    const system = `${STYLIST_SYSTEM_INSTRUCTION}${personaInstruction}${personalization}`;

    try {
        const genModel = ai.getGenerativeModel({ model });
        const chat = genModel.startChat({
            history,
            generationConfig: {
                maxOutputTokens: 2048,
            }
        });

        const result = await chat.sendMessage(messageParts);
        const text = result.response.text();

        return {
            text,
            history: [...history, { role: 'user', parts: messageParts }, { role: 'model', parts: [{ text }] }]
        };
    } catch (error) {
        Logger.error('Error in unified styling advice', error);
        throw error;
    }
}
